import { formatPrice } from '@/utils/format';

interface TrendDataPoint {
  date: string;
  dateLabel: string;
  orders: number;
  revenue: number;
}

interface TrendChartProps {
  data: TrendDataPoint[];
  period: 'today' | 'week' | 'month';
}

export function TrendChart({ data, period }: TrendChartProps) {
  if (data.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
        <p className="text-gray-500">Veri yok</p>
      </div>
    );
  }

  const maxRevenue = Math.max(...data.map((d) => d.revenue), 1);
  const maxOrders = Math.max(...data.map((d) => d.orders), 1);
  const chartHeight = 200;
  const chartWidth = 600;
  const padding = 40;

  const revenuePoints = data.map((d, index) => {
    const x = padding + (index / (data.length - 1 || 1)) * (chartWidth - 2 * padding);
    const y = padding + chartHeight - (d.revenue / maxRevenue) * (chartHeight - 2 * padding);
    return { x, y, ...d };
  });

  const ordersPoints = data.map((d, index) => {
    const x = padding + (index / (data.length - 1 || 1)) * (chartWidth - 2 * padding);
    const y = padding + chartHeight - (d.orders / maxOrders) * (chartHeight - 2 * padding);
    return { x, y, ...d };
  });

  const revenuePath = revenuePoints
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ');

  const ordersPath = ordersPoints
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ');

  const formatDateLabel = (item: TrendDataPoint) => {
    if (item.dateLabel) {
      if (period === 'today') {
        const timeMatch = item.dateLabel.match(/\d{2}:\d{2}/);
        return timeMatch ? timeMatch[0] : item.dateLabel;
      }
      const dateMatch = item.dateLabel.match(/(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        return `${dateMatch[3]}.${dateMatch[2]}`;
      }
      return item.dateLabel;
    }
    const date = new Date(item.date);
    if (period === 'today') {
      return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    }
    return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}`;
  };

  return (
    <div className="overflow-x-auto">
      <svg
        width={chartWidth}
        height={chartHeight + 60}
        className="w-full"
        viewBox={`0 0 ${chartWidth} ${chartHeight + 60}`}
      >
        <defs>
          <linearGradient id="revenueGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#E63946" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#E63946" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="ordersGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#2A9D8F" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#2A9D8F" stopOpacity="0" />
          </linearGradient>
        </defs>

        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = padding + chartHeight - ratio * (chartHeight - 2 * padding);
          return (
            <g key={ratio}>
              <line
                x1={padding}
                y1={y}
                x2={chartWidth - padding}
                y2={y}
                stroke="#e5e7eb"
                strokeWidth="1"
                strokeDasharray="4 4"
              />
              <text x={padding - 10} y={y + 4} textAnchor="end" fontSize="12" fill="#6b7280">
                {formatPrice(maxRevenue * ratio)}
              </text>
            </g>
          );
        })}

        <path
          d={`${revenuePath} L ${revenuePoints[revenuePoints.length - 1].x} ${padding + chartHeight} L ${revenuePoints[0].x} ${padding + chartHeight} Z`}
          fill="url(#revenueGradient)"
        />

        <path
          d={revenuePath}
          fill="none"
          stroke="#E63946"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        <path
          d={ordersPath}
          fill="none"
          stroke="#2A9D8F"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="4 4"
        />

        {revenuePoints.map((point, index) => (
          <g key={index}>
            <circle cx={point.x} cy={point.y} r="4" fill="#E63946" />
            <title>
              {formatDateLabel(point)}: {formatPrice(point.revenue)} ({point.orders} sipariş)
            </title>
          </g>
        ))}

        {revenuePoints.map((point, index) => {
          if (period === 'today' && index % Math.ceil(revenuePoints.length / 8) !== 0) {
            return null;
          }
          if ((period === 'week' || period === 'month') && index % Math.ceil(revenuePoints.length / 7) !== 0) {
            return null;
          }
          return (
            <text
              key={index}
              x={point.x}
              y={chartHeight + padding + 20}
              textAnchor="middle"
              fontSize="11"
              fill="#6b7280"
            >
              {formatDateLabel(point)}
            </text>
          );
        })}
      </svg>

      <div className="flex items-center justify-center gap-6 mt-4">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-primary rounded"></div>
          <span className="text-sm text-gray-600">Gelir</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-secondary rounded" style={{ borderStyle: 'dashed' }}></div>
          <span className="text-sm text-gray-600">Sipariş</span>
        </div>
      </div>
    </div>
  );
}
